import React, { useEffect, useState } from "react";
import { Volume2, KeyRound, RefreshCw, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { D, inputStyle, Btn, Chip, Field } from "./ui";

// Pengaturan narator radio: saklar sumber suara (Universal Key via fal.ai ↔ API key ElevenLabs sendiri)
export function PengaturanNarator({ token, setMsg }) {
  const [n, setN] = useState(null);
  const [apiKey, setApiKey] = useState("");          // key baru yang diketik (kosong = tidak diubah)
  const [sibuk, setSibuk] = useState("");
  useEffect(() => { api.getNarator(token).then(setN).catch(e => setMsg(e.message)); }, [token]); // eslint-disable-line
  if (!n) return null;

  const data = (denganKey) => ({
    sumber: n.sumber, voice: n.voice, voice_id: n.voice_id, model_id: n.model_id, stability: Number(n.stability),
    pembuka: n.pembuka || "", penutup: n.penutup || "", ...(denganKey && apiKey.trim() ? { api_key: apiKey.trim() } : {}),
  });
  const simpan = async () => {
    setSibuk("simpan");
    try { setN(await api.patchNarator(token, data(true))); setApiKey(""); setMsg("Pengaturan narator tersimpan"); } catch (e) { setMsg(e.message); }
    setSibuk("");
  };
  const uji = async () => {
    setSibuk("uji");
    try { const r = await api.ujiNarator(token, data(true)); new Audio(`data:audio/mpeg;base64,${r.audio_b64}`).play(); setMsg(`Contoh suara (${r.sumber === "elevenlabs" ? "ElevenLabs saya" : "Universal Key"}) diputar`); }
    catch (e) { setMsg(e.message); }
    setSibuk("");
  };
  const hapusKey = async () => {
    if (!window.confirm("Hapus API key ElevenLabs yang tersimpan?")) return;
    try { setN(await api.patchNarator(token, { api_key: "" })); setApiKey(""); setMsg("API key dihapus"); } catch (e) { setMsg(e.message); }
  };
  const el = n.sumber === "elevenlabs";

  return (
    <div data-testid="narator-settings" style={{ display: "flex", flexDirection: "column", gap: 12, padding: 14, background: D.cream, borderRadius: 18, marginBottom: 16 }}>
      <SaklarSumber value={n.sumber} onChange={v => setN({ ...n, sumber: v })} adaKey={n.api_key_tersimpan} />
      {el
        ? <PanelElevenLabs n={n} setN={setN} token={token} apiKey={apiKey} setApiKey={setApiKey} hapusKey={hapusKey} setMsg={setMsg} />
        : <PanelUniversal n={n} setN={setN} />}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label={`Stabilitas (${Number(n.stability).toFixed(2)}) — rendah = lebih ekspresif${el && n.model_id === "eleven_v3" ? " · v3 dibulatkan ke 0 / 0.5 / 1" : ""}`}>
          <input data-testid="narator-stability" type="range" min="0" max="1" step="0.05" value={n.stability} onChange={e => setN({ ...n, stability: e.target.value })} style={{ width: "100%" }} />
        </Field>
        <span />
        <Field label="Pembuka (dibacakan sebelum tiap naskah)"><input data-testid="narator-pembuka" value={n.pembuka || ""} onChange={e => setN({ ...n, pembuka: e.target.value })} placeholder='mis. "[warm] Radio Jawsel. "' style={inputStyle} /></Field>
        <Field label="Penutup"><input data-testid="narator-penutup" value={n.penutup || ""} onChange={e => setN({ ...n, penutup: e.target.value })} placeholder='mis. " Tetap di Radio Jawsel."' style={inputStyle} /></Field>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Btn testid="narator-simpan" onClick={simpan} disabled={!!sibuk}>{sibuk === "simpan" ? "Menyimpan..." : "Simpan narator"}</Btn>
        <Btn variant="ghost" testid="narator-uji" onClick={uji} disabled={!!sibuk}><Volume2 size={14} /> {sibuk === "uji" ? "Membuat contoh..." : "Tes suara"}</Btn>
        <span style={{ fontSize: 12, color: D.sub }}>Tag ekspresi v3: [warm] [cheerful] [whispers] [laughs]</span>
      </div>
    </div>
  );
}

function SaklarSumber({ value, onChange, adaKey }) {
  const opsi = [["universal", "Universal Key", "ElevenLabs v3 via fal.ai · tanpa key"], ["elevenlabs", "ElevenLabs saya", adaKey ? "API key tersimpan" : "butuh API key akun sendiri"]];
  return (
    <div data-testid="narator-sumber" role="radiogroup" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: 4, background: "#fff", borderRadius: 16, border: `1.5px solid ${D.line}` }}>
      {opsi.map(([v, judul, ket]) => {
        const on = value === v;
        return (
          <button key={v} type="button" role="radio" aria-checked={on} data-testid={`narator-sumber-${v}`} onClick={() => onChange(v)}
            style={{ border: 0, borderRadius: 12, padding: "10px 12px", textAlign: "left", cursor: "pointer", background: on ? D.blue : "transparent", color: on ? "#fff" : D.ink, transition: "background-color .15s, color .15s" }}>
            <b style={{ display: "block", fontSize: 13 }}>{judul}</b>
            <span style={{ fontSize: 11.5, opacity: .8 }}>{ket}</span>
          </button>
        );
      })}
    </div>
  );
}

function PanelUniversal({ n, setN }) {
  const bawaan = n.fal_voices.some(v => v.id === n.voice);
  const [kustom, setKustom] = useState(!bawaan);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      <Field label="Suara bawaan fal.ai">
        <select data-testid="narator-voice-select" value={kustom ? "__kustom" : n.voice} style={inputStyle}
          onChange={e => { if (e.target.value === "__kustom") { setKustom(true); } else { setKustom(false); setN({ ...n, voice: e.target.value }); } }}>
          {n.fal_voices.map(v => <option key={v.id} value={v.id}>{v.nama} — {v.ket}</option>)}
          <option value="__kustom">Voice ID kustom…</option>
        </select>
      </Field>
      {kustom && (
        <Field label="Voice ID ElevenLabs (publik)">
          <input data-testid="narator-voice" value={n.voice} onChange={e => setN({ ...n, voice: e.target.value })} placeholder="mis. RbNgJzKAV7jpYJNtCBpj" style={inputStyle} />
        </Field>
      )}
    </div>
  );
}

function PanelElevenLabs({ n, setN, token, apiKey, setApiKey, hapusKey, setMsg }) {
  const [voices, setVoices] = useState(null);
  const [memuat, setMemuat] = useState(false);
  const ambil = async () => {
    setMemuat(true);
    try { const r = await api.voicesElevenLabs(token, apiKey.trim() || undefined); setVoices(r.voices); setMsg(`${r.voices.length} voice ditemukan`); } catch (e) { setMsg(e.message); }
    setMemuat(false);
  };
  const terpilih = voices?.find(v => v.voice_id === n.voice_id);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      <Field label="API key ElevenLabs" grow>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input data-testid="narator-api-key" type="password" autoComplete="off" value={apiKey} onChange={e => setApiKey(e.target.value)}
            placeholder={n.api_key_tersimpan ? `Tersimpan ${n.api_key_hint} — ketik untuk mengganti` : "sk_… dari elevenlabs.io/app/settings/api-keys"} style={inputStyle} />
          {n.api_key_tersimpan && <button type="button" data-testid="narator-api-key-hapus" onClick={hapusKey} title="Hapus key tersimpan" style={{ width: 36, height: 36, flex: "none", borderRadius: 10, border: `1.5px solid ${D.dangerSoft}`, background: "#fff", color: D.danger, display: "grid", placeItems: "center", cursor: "pointer" }}><Trash2 size={14} /></button>}
        </div>
      </Field>
      <Field label="Model">
        <select data-testid="narator-model" value={n.model_id} onChange={e => setN({ ...n, model_id: e.target.value })} style={inputStyle}>
          <option value="eleven_v3">Eleven v3 (paling ekspresif, tag emosi)</option>
          <option value="eleven_multilingual_v2">Multilingual v2 (stabil)</option>
        </select>
      </Field>
      <Field label="Voice ID">
        <input data-testid="narator-voice-id" value={n.voice_id} onChange={e => setN({ ...n, voice_id: e.target.value })} placeholder="RbNgJzKAV7jpYJNtCBpj" style={inputStyle} />
      </Field>
      <Field label="Pilih dari akun ElevenLabs">
        <div style={{ display: "flex", gap: 6 }}>
          {voices
            ? <select data-testid="narator-voices-akun" value={terpilih ? n.voice_id : ""} onChange={e => e.target.value && setN({ ...n, voice_id: e.target.value })} style={inputStyle}>
                <option value="">{terpilih ? "" : "— pilih voice —"}</option>
                {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.nama}{v.ket ? ` — ${v.ket}` : ""}</option>)}
              </select>
            : <span style={{ ...inputStyle, display: "flex", alignItems: "center", color: D.sub }}>{n.api_key_tersimpan || apiKey ? "Klik ambil untuk memuat daftar" : "Isi API key dulu"}</span>}
          <span style={{ flex: "none" }}>
            <Btn variant="ghost" testid="narator-voices-ambil" onClick={ambil} disabled={memuat || (!n.api_key_tersimpan && !apiKey.trim())}>
              <RefreshCw size={13} /> {memuat ? "Memuat…" : "Ambil"}
            </Btn>
          </span>
        </div>
      </Field>
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Chip tone={n.api_key_tersimpan ? "green" : "amber"} testid="narator-key-status"><KeyRound size={10} style={{ verticalAlign: -1, marginRight: 4 }} />{n.api_key_tersimpan ? `Key tersimpan ${n.api_key_hint}` : "Key belum tersimpan"}</Chip>
        {terpilih?.preview_url && <audio data-testid="narator-voice-preview" controls src={terpilih.preview_url} style={{ height: 30 }} />}
        <span style={{ fontSize: 12, color: D.sub }}>Key disimpan terenkripsi di database, tidak pernah dikirim balik ke browser.</span>
      </div>
    </div>
  );
}
